import { Injectable, Signal, computed } from '@angular/core'
import { toSignal } from '@angular/core/rxjs-interop'
import { UserGroupPermissionId } from '@ftr/contracts/api/user-group'
import { Icon, unwrapData } from '@ftr/foundation'
import { AppPaths, OrderPaths } from '@ftr/routing-paths'
import { UserState } from '@ftr/ui-user'
import { Store } from '@ngxs/store'
import { combineLatest, map } from 'rxjs'

export type SideNavItem =
  | {
      type: 'LINK'
      title: string
      icon: Icon
      tooltipHint?: string
      path: string[]
    }
  | { type: 'BUTTON'; onSelect: () => void; title: string; icon: Icon; tooltipHint?: string }

export interface SideNavGroup {
  title?: string
  position?: 'footer'
  items?: SideNavItem[]
}

const DEFAULT_NAV_ITEMS = {
  'transcript-jobs': {
    type: 'LINK',
    title: 'Transcript Jobs',
    path: [`/${AppPaths.Jobs}`],
    icon: 'TranscriptJobs',
  } as SideNavItem,
  'upload-and-play': {
    type: 'LINK',
    title: 'Upload & Play',
    path: [`/${AppPaths.Uploads}`],
    icon: 'UploadAndPlay',
  } as SideNavItem,
  'my-orders': {
    type: 'LINK',
    title: 'My Orders',
    path: [`/${AppPaths.Orders}`],
    icon: 'MyOrders',
  } as SideNavItem,
  'shared-recordings': {
    type: 'LINK',
    title: 'Shared Recordings',
    path: [`/${AppPaths.SharedRecordings}`],
    icon: 'SharedRecordings',
  } as SideNavItem,
  'order-audio': {
    type: 'LINK',
    title: 'Audio',
    path: [`/${AppPaths.Orders}`, OrderPaths.ChooseCourt, OrderPaths.Audio],
    icon: 'OrdersAudio',
    tooltipHint: 'Audio Orders',
  } as SideNavItem,
  'order-real-time': {
    type: 'LINK',
    title: 'Real-time',
    path: [`/${AppPaths.Orders}`, OrderPaths.ChooseCourt, OrderPaths.RealTime],
    icon: 'OrdersRealtime',
    tooltipHint: 'Real-time Orders',
  } as SideNavItem,
  'order-transcript': {
    type: 'LINK',
    title: 'Transcript',
    path: [`/${AppPaths.Orders}`, OrderPaths.ChooseCourt, OrderPaths.Transcript],
    icon: 'OrdersTranscript',
    tooltipHint: 'Transcript Orders',
  } as SideNavItem,
}

@Injectable({
  providedIn: 'root',
})
export class SideNavItemsProvider {
  constructor(private store: Store) {}

  provide(currentCourtSystemId: Signal<string | undefined>): Signal<{ navGroups: SideNavGroup[] }> {
    const courtSystemsAndPermission$ = combineLatest([
      this.store.select(UserState.courtSystems).pipe(unwrapData()),
      this.store
        .select(UserState.hasPermissionInAnyCourtSystem)
        .pipe(map(fn => fn(UserGroupPermissionId.TranscribeRecordings))),
    ])
    const courtSystemsAndPermission = toSignal(courtSystemsAndPermission$, { initialValue: [[], false] })

    return computed(() => {
      const [courtSystems, hasTranscriberPermissions] = courtSystemsAndPermission()
      const currentCourtSystem = currentCourtSystemId()

      if (currentCourtSystem) {
        return {
          navGroups: [
            { items: [{ type: 'LINK', title: 'Exit Court System', icon: 'Cancel', path: ['/'] } as SideNavItem] },
          ],
        }
      }
      return {
        navGroups: [
          ...(hasTranscriberPermissions ? [{ items: [DEFAULT_NAV_ITEMS['transcript-jobs']] }] : []),
          {
            title: 'My content',
            items: [
              DEFAULT_NAV_ITEMS['upload-and-play'],
              DEFAULT_NAV_ITEMS['my-orders'],
              DEFAULT_NAV_ITEMS['shared-recordings'],
            ],
          },
          {
            title: 'Place an order',
            items: [
              DEFAULT_NAV_ITEMS['order-audio'],
              DEFAULT_NAV_ITEMS['order-real-time'],
              DEFAULT_NAV_ITEMS['order-transcript'],
            ],
          },
          {
            position: 'footer',
            items: [
              ...(courtSystems.length > 0
                ? [
                    {
                      type: 'LINK',
                      title: 'Select a court system',
                      path: ['/court-system'],
                      icon: 'Gavel',
                    } as SideNavItem,
                  ]
                : []),
              {
                type: 'BUTTON',
                title: 'Help & Support',
                onSelect: () => console.log('Open help centre'),
                icon: 'HelpCircle',
              } as SideNavItem,
            ],
          },
        ],
      }
    })
  }
}
